/* De la sigla de la planilla al nombre de la empresa.
 *
 * ── EL PROBLEMA: LA MISMA EMPRESA SE ESCRIBE DE CINCO FORMAS ──────────────
 *
 * Transcan figura como `TNSC` en la planilla de vencimientos, `TRNSC` cuando
 * alguien la escribe entera, `TRC` que es como se la nombraba, `TCAN` en las
 * planillas de movimientos, y `TCC`. Son la misma empresa. Igual el fletero:
 * `FLT` en la planilla y `FL` en el habla.
 *
 * ── Y LA CANÓNICA AHORA ES `TCC` ─────────────────────────────────────────
 *
 * Lo bajó Nicolás: *«hay que estandarizar todas las siglas de Transcan a TCC.
 * No más TRC, TRCAN, ni ninguna otra.»* Antes la canónica era `TRC` y convivía
 * con las otras cuatro según de qué planilla saliera la fila.
 *
 * **LOS ALIAS DE ENTRADA NO SE BORRAN**, y eso es lo que hace que el cambio
 * sea seguro: `TNSC` es lo que dice la planilla de vencimientos y `TCAN` el
 * Excel de movimientos, y esos archivos no los controlamos nosotros. Se acepta
 * todo lo que se escribió alguna vez y se muestra una sola cosa.
 *
 * **Y `TC` NO ES TRANSCAN.** Es Transportes Cantarini, otra empresa, con otro
 * CUIT y con 432 tractores contra los 10 de Transcan. Se parece a una
 * abreviatura y no lo es: mapearla acá fusionaría las dos empresas más grandes
 * de la flota en cada pantalla, sin fallar en ningún lado.
 *
 * **Acá SÍ se unifican, y en los clientes NO.** Parece una contradicción con
 * la regla de `movimientos` —«nadie fusiona RADIO VICTORIA con R.VICTORIA»— y
 * no lo es: aquéllos son 256 nombres abiertos donde una sucursal puede ser un
 * destino distinto y unirlos arruinaría las estadísticas de las dos. Esto es
 * un glosario **cerrado de cinco empresas**, confirmado por quien las factura.
 * Cuando el vocabulario es cerrado y alguien lo confirmó, unificar es traducir;
 * cuando es abierto y nadie lo confirmó, unificar es inventar.
 *
 * ── LA FORMA CANÓNICA NO SE ELIGE ACÁ ────────────────────────────────────
 *
 * Sale de `empresa_catalog.GLOSARIO` de FlotaBot, que ya tiene TA / TC / EX /
 * TCC con su razón social y su CUIT y lo usa el mapa. Con dos vocabularios, el
 * mapa diría «TCC» y esta pantalla «TNSC» de la misma empresa, y nadie sabría
 * que son una. Si allá se agrega una, se agrega acá.
 *
 * ── FL NO ES UNA EMPRESA DEL GRUPO ───────────────────────────────────────
 *
 * Es «el camión no es nuestro». Por eso no está en aquel glosario —no tiene
 * CUIT propio, son muchos— y por eso acá se dice «Fletero» y no un nombre
 * propio: son 52 de 224, y ponerles el nombre de una empresa haría creer que
 * es una sola.
 *
 * ── Y LO QUE NO SE RECONOCE SE MUESTRA ───────────────────────────────────
 *
 * Con su sigla tal cual, no vacío. Es la misma regla que `de_cuit` usa con los
 * 25 tractores cuyo CUIT no está en la lista: callarlo haría ver a ese chofer
 * como si no tuviera empresa, que es lo contrario de lo que dice el dato.
 */

/* Todas las formas en que aparece escrita cada una → la canónica.
 * Se compara normalizado (sin puntos, espacios ni acentos). */
const ALIAS = {
  TC: "TC", TRANSPORTESCANTARINI: "TC",
  EX: "EX", EXPRESO: "EX", EXPRESOCANTARINI: "EX",
  TA: "TA", TRANSAMERICA: "TA", TRANSAMERICATRANSPORTES: "TA",
  TCC: "TCC", TRC: "TCC", TNSC: "TCC", TRNSC: "TCC", TCAN: "TCC",
  TRANSCAN: "TCC", TRANSCANCARGAS: "TCC",
  JK: "JK", JOWKON: "JK",
  FL: "FL", FLT: "FL", FLETERO: "FL", FLETEROS: "FL",
  IF: "IF", INVERSIONESFUEGUINAS: "IF",
  NOV: "NOV", NOVELLI: "NOV",
  CEJAS: "CEJAS",
};

/* La sigla canónica → cómo se dice. Los cuatro primeros son los de
 * `empresa_catalog.GLOSARIO`; el quinto no es una empresa (ver arriba). */
export const EMPRESAS = {
  TC: { nombre: "Transportes Cantarini", propia: true },
  EX: { nombre: "Expreso Cantarini", propia: true },
  TA: { nombre: "Trans-America Transportes", propia: true },
  TCC: { nombre: "Transcan Cargas", propia: true },
  /* Ticket 206 (Cuffone, 18/09/2026, urgente): «FALTA EMPRESA — JOW KON, CUIT
   * 33-71405268-9. Padrones choferes y personal, para diferenciar en cuál
   * empresa trabaja». Es empleadora del grupo, así que `propia: true` para que
   * el desplegable del alta la ofrezca en los dos padrones. Gemela en
   * `empresa_catalog.GLOSARIO` de FlotaBot — si se agrega acá, se agrega allá. */
  JK: { nombre: "Jow Kon", propia: true },
  FL: { nombre: "Fletero", propia: false },
  /* Contestada por Aldo el 31/08/2026: «IF es INVERSIONES FUEGUINAS, es otra
   * empresa que ya usamos poco pero algunos camiones todavia estan afectados».
   *
   * `propia: false` A PROPÓSITO, y no porque sea de un tercero: `propia`
   * decide qué se ofrece en el desplegable al asignarle empresa a una unidad,
   * y una empresa que se está dejando de usar no es donde tiene que nacer la
   * próxima. Las que ya están afectadas se siguen viendo con su nombre.
   *
   * NO TIENE CUIT ACÁ, y por eso no está en `POR_CUIT`: no lo sabemos. Las
   * unidades 358 y 369 guardan la sigla «IF» pelada y por ahí se reconocen. */
  IF: { nombre: "Inversiones Fueguinas", propia: false },
};

/* ── LOS QUE NO SON EMPRESAS: FLETEROS CON NOMBRE ─────────────────────────
 *
 * También de la respuesta de Aldo: «NOV, es porque en su momento NOVELLI era
 * fletero y usaba ese camion, no le des bola» y «CEJAS es un fletero».
 *
 * Van separados de `EMPRESAS` porque no lo son —el camión no era nuestro— y
 * separados de `FL` porque `FL` dice «fletero» sin decir cuál, que es lo
 * correcto cuando son 52; acá sabemos el nombre y esconderlo perdería el
 * único dato que la planilla traía.
 *
 * Y no se corrige la unidad: la 115 sigue siendo de Expreso Cantarini en el
 * padrón —que es lo que dice su CUIT— y CEJAS era quien la manejaba. Ese
 * choque quedó resuelto a favor del padrón, no pisado. */
export const FLETEROS = {
  NOV: "Novelli",
  CEJAS: "Cejas",
};

/* \u2500\u2500 Y LOS PADRONES NO GUARDAN LA SIGLA: GUARDAN EL CUIT \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
 *
 * `tractores.empresa`, `furgones.empresa` y `personas.empresa` traen
 * `30610890403` pelado. Nadie reconoce a Expreso Cantarini por su n\u00famero, as\u00ed
 * que sin esto la columna Empresa del RTO ser\u00eda una fila de once d\u00edgitos.
 *
 * GEMELO DE `empresa_catalog._POR_CUIT` en FlotaBot, que es de donde salen los
 * cuatro CUIT \u2014 no se tipean de memoria. Si toc\u00e1s una, toc\u00e1 la otra.
 *
 * Medido en `furgones`: TC 204 \u00b7 EX 50 \u00b7 TA 37 \u00b7 TCC 16, y **372 sin empresa**
 * de 679. */
const POR_CUIT = {
  30683612916: "TA",
  30610890403: "TC",
  30644511304: "EX",
  33719349949: "TCC",
  33714052689: "JK", // Jow Kon — ticket 206
};

/* \u00ab0\u00bb, \u00abfalse\u00bb y vac\u00edo NO son una empresa: son c\u00f3mo qued\u00f3 el campo seg\u00fan de
 * qu\u00e9 migraci\u00f3n sali\u00f3 la fila. Son 334 furgones en \u00ab0\u00bb \u2014 le\u00eddos como una
 * empresa llamada \u00ab0\u00bb, la columna tendr\u00eda un valor inventado con 334 unidades
 * adentro y el filtro ofrecer\u00eda elegirlo. */
const VACIOS = new Set(["", "0", "FALSE", "NULL", "-"]);

function normalizar(v) {
  return String(v ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

/* La sigla canónica, o `null` si la celda está vacía, o la sigla TAL CUAL se
 * escribió cuando no la reconocemos. Tres respuestas y no dos: «no tiene
 * empresa cargada» y «tiene una que no conozco» mandan a hacer cosas
 * distintas — la primera se completa, la segunda se agrega al glosario. */
export function siglaDe(valor) {
  const n = normalizar(valor);
  if (!n || VACIOS.has(n)) return null;
  /* El CUIT de los padrones. Va antes que el alias porque son once dígitos y
   * ningún alias lo es — y un CUIT desconocido tiene que caer al `return` de
   * abajo y mostrarse, no desaparecer. */
  if (POR_CUIT[n]) return POR_CUIT[n];
  return ALIAS[n] || String(valor).trim().toUpperCase();
}

/* El CUIT con guiones, para cuando no lo reconocemos y hay que mostrarlo.
 * `30708633174` —25 tractores— no es ninguno de los cuatro: puede ser una
 * sociedad del grupo sin cargar o un dato viejo, y esconderlo haría que esas
 * unidades se vean como si no tuvieran empresa, que es lo contrario de lo que
 * dice el dato. Gemelo de `empresa_catalog._cuit_lindo`. */
export function cuitLindo(valor) {
  const d = String(valor ?? "").replace(/\D/g, "");
  return d.length === 11 ? `${d.slice(0, 2)}-${d.slice(2, 10)}-${d.slice(10)}` : d;
}

/* Lo que se dibuja en una celda angosta: la sigla, o el CUIT formateado si no
 * está en el glosario, o «—» si no hay empresa. Tres respuestas y no dos. */
export function etiquetaCorta(valor) {
  const s = siglaDe(valor);
  if (!s) return "—";
  if (EMPRESAS[s]) return s;
  return /^\d{11}$/.test(s) ? cuitLindo(s) : s;
}

/* ── LA SIGLA CON SU CUIT AL LADO ─────────────────────────────────────────
 *
 * Pedido de Nico (28/08/2026): *«al lado de la empresa (TC, EX, TA, TCC) esté
 * el CUIT que le corresponde»*.
 *
 * VA DONDE HAY LUGAR Y NO EN UNA CELDA. Once dígitos más dos guiones no entran
 * en el chip de una lista de 400 filas: la columna Empresa pasaría a ser la
 * más ancha de la tabla para repetir cuatro números. Por eso `etiquetaCorta`
 * se queda como está —en las listas va la sigla sola— y esto se usa donde se
 * mira UNA unidad o UNA persona: la ficha.
 *
 * Y NO SE REPITE CUANDO NO SE SABE: un CUIT que no está en el glosario ya es
 * todo lo que `etiquetaCorta` muestra, así que sale una sola vez. `FL` no es
 * una empresa del grupo y no tiene CUIT propio — sale «Fletero» a secas. */
const CUIT_POR_SIGLA = Object.fromEntries(
  Object.entries(POR_CUIT).map(([cuit, sigla]) => [sigla, cuit]),
);

export function cuitDe(valor) {
  const s = siglaDe(valor);
  if (!s) return "";
  if (/^\d{11}$/.test(s)) return cuitLindo(s);
  return CUIT_POR_SIGLA[s] ? cuitLindo(CUIT_POR_SIGLA[s]) : "";
}

export function etiquetaConCuit(valor, separador = " · ") {
  /* Con la duda incluida: la ficha es justamente donde alguien se va a
   * preguntar de quién es este camión, así que es el peor lugar para mostrar
   * una sigla sin confirmar como si estuviera confirmada. Ver `POR_CONFIRMAR`.
   * Y las que no están confirmadas no tienen CUIT —por eso no las conocemos—
   * así que el signo queda solo, sin número al lado. */
  const et = etiquetaCortaConDuda(valor);
  if (et === "—") return et;
  const cuit = cuitDe(valor);
  return cuit && cuit !== et ? `${et}${separador}${cuit}` : et;
}

/* ── LAS SIGLAS QUE NADIE PODÍA EXPLICAR, Y YA ESTÁN CONTESTADAS ──────────
 *
 * Ticket 155 punto 1. La planilla de Aldo usaba tres siglas que no estaban en
 * el glosario —`IF` (4 unidades), `NOV` (1) y `CEJAS` (1)— y se mostraban con
 * un signo de pregunta para que la pregunta siguiera viva. `padron_empresas`
 * la mandó por mail, y ALDO CONTESTÓ el 31/08/2026:
 *
 *   · IF    → Inversiones Fueguinas. Es una empresa, y está arriba.
 *   · NOV   → Novelli, que era fletero y usaba ese camión. Está en `FLETEROS`.
 *   · CEJAS → un fletero. Ídem.
 *
 * Así que el conjunto queda VACÍO y no se borra: el signo de pregunta es
 * maquinaria que sirve —la próxima sigla que aparezca en una planilla se
 * agrega acá y vuelve a verse— y borrarla obligaría a reescribirla el día que
 * haga falta, que es cuando nadie se acuerda de que existía.
 *
 * NO SE INVENTA LA EQUIVALENCIA, y sigue valiendo: lo que hay arriba lo dijo
 * Aldo, no lo dedujo esta pantalla.
 *
 * LO QUE ESTO NO CONTESTA son los dos choques que quedan, y por eso no se
 * tocan las unidades: la 376 tiene en el padrón un CUIT sin identificar
 * (30-70863317-4, que llevan 25 tractores) y la 385 tiene el de Trans-America,
 * y las dos dicen `IF` en la planilla. «Están afectados» no alcanza para
 * reemplazar una empresa con CUIT por una sigla.
 */
export const POR_CONFIRMAR = new Set([]);

export function aConfirmar(valor) {
  const s = siglaDe(valor);
  return s !== null && POR_CONFIRMAR.has(s);
}

/* La sigla con el signo de pregunta cuando no está confirmada. Es lo que va en
 * una celda: `etiquetaCorta` sigue devolviendo la sigla pelada para quien
 * necesite compararla o filtrar por ella, porque un filtro que ofrezca «IF?» y
 * otra pantalla que guarde «IF» no emparejan nunca. */
export function etiquetaCortaConDuda(valor) {
  const et = etiquetaCorta(valor);
  return aConfirmar(valor) ? `${et}?` : et;
}

/* Cómo se dice. Sin empresa cargada NO devuelve "" —un hueco se lee como una
 * columna que no cargó— y una sigla desconocida devuelve la sigla. */
export function nombreDe(valor) {
  const s = siglaDe(valor);
  if (!s) return "Sin empresa";
  if (EMPRESAS[s]) return EMPRESAS[s].nombre;
  /* En el `title` sí se explica: la celda tiene lugar para un signo y el
   * globo tiene lugar para la frase, y sin la frase el signo se lee como un
   * error de la pantalla. */
  if (FLETEROS[s]) return `${FLETEROS[s]} — fletero`;
  return POR_CONFIRMAR.has(s)
    ? `${s} — sigla de la planilla, sin confirmar qué empresa es`
    : s;
}

/* ── LA ETIQUETA DE UNA FICHA, CON EL CATÁLOGO DE PROVEEDORES DE RESPALDO ──
 *
 * Igna, 15/09/2026, con la ficha de un alta recién hecha en la mano: *«figura
 * en empresa TAYLOR, ¿por qué?»*.
 *
 * Porque la ficha no estaba mirando el glosario: tomaba el nombre de la fila
 * de `empresas`, y ahí los CUIT de dos de las cuatro propias están cargados
 * como proveedores con otro nombre —**TA es «TAYLOR» y TC es «FILTER GROUP
 * S.A»**, con `codigo` 56 y 57, que es como las usa el módulo de repuestos—.
 * El alta estaba bien: el CUIT era el correcto. Lo que estaba mal era el
 * cartel.
 *
 * Y NO ERA UNA FICHA: eran 1.270 al 15/09 —105 personas, 84 tractores y 79
 * furgones en TA, más 179, 414 y 409 en TC—. O sea que más de la mitad de la
 * flota decía pertenecer a una empresa que no es, y el que abría la ficha de
 * un camión propio leía el nombre de un proveedor.
 *
 * EL GLOSARIO MANDA Y EL CATÁLOGO COMPLETA. Para las del grupo sale la sigla
 * con su CUIT, que es como el resto del hub las nombra; para todo lo demás
 * —proveedores, terceros— sigue saliendo lo que dice la fila de `empresas`,
 * que ahí sí es el único lugar donde está el nombre.
 *
 * NO SE ARREGLA TOCANDO ESAS DOS FILAS de `empresas`: el módulo de repuestos
 * las busca por su código de proveedor. Es el mismo motivo por el que el
 * desplegable del alta ya sale de acá y no de esa tabla.
 */
export function etiquetaDeFicha(valor, fila = null) {
  const s = siglaDe(valor);
  if (s && EMPRESAS[s]) return etiquetaConCuit(valor);
  if (fila) return `${fila.nombre} (${fila.id})`;
  /* Sin fila y sin glosario, lo que haya: un CUIT que no reconocemos se
   * muestra formateado y no se esconde — esconderlo haría ver la unidad como
   * si no tuviera empresa, que es lo contrario de lo que dice el dato. */
  return s ? etiquetaCorta(valor) : "";
}

/* ── EL COLOR DE CADA UNA ─────────────────────────────────────────────────
 *
 * Lo pidió Igna para la ficha: *«al lado del interno que aparezca una cajita
 * con las siglas dependiendo de la empresa»*. Los colores los corrigió Nico
 * (08/09/2026): TA verde, TC azul, EX rojo, Transcan violeta.
 *
 * SON LOS MISMOS QUE LAS CAJAS DE HERRAMIENTAS (Igna, 02/10/2026: «unificá
 * los colores como en las cajas», y enseguida «EX es ROJO y TA es VERDE»):
 * `COLORES_POR_EMPRESA` en `shared/herramientas`. Si se cambia uno, se cambia
 * el otro —`coloresEmpresa.test.js` falla si no—: dos colores para la misma
 * empresa es lo que esto vino a sacar.
 *
 * **Va con el glosario y no en la hoja de estilos de una pantalla.** De quién
 * es un camión es la misma pregunta en la ficha, en el RTO y en el padrón, y
 * el día que se dibuje en la segunda con otro color las dos dirían cosas
 * distintas del mismo dato. Acá se decide CUÁL es el color; el CSS decide qué
 * tono, que es lo que permite que el tema oscuro no lo rompa.
 *
 * **La clave es la CANÓNICA y no una de las formas de entrada.** Hoy es `TCC`;
 * cuando era `TRC` este comentario advertía justamente eso — poner el color en
 * una de las otras cuatro formas no pintaría nada, porque para cuando llega acá
 * ya se tradujo. Sale de `empresa_catalog.GLOSARIO` de FlotaBot.
 *
 * **FL no lleva color, y no es un olvido.** No es una empresa del grupo sino
 * «el camión no es nuestro» — son muchos fleteros distintos, y un color propio
 * los haría ver como una quinta empresa. Lo mismo lo que no reconocemos: un
 * color inventado sobre una sigla desconocida la disfrazaría de conocida, que
 * es justo lo que `desconocidas()` existe para evitar.
 */
const COLOR = { TA: "ta", TC: "tc", EX: "ex", TCC: "tcc" };

export function claseDeEmpresa(valor) {
  const s = siglaDe(valor);
  return s && COLOR[s] ? `emp emp--${COLOR[s]}` : "emp emp--otra";
}

/* ¿La conocemos? Lo usa la pantalla para listar aparte las que hay que sumar
 * al glosario, en vez de dejarlas pasar disfrazadas de nombre propio. */
export const esConocida = (valor) => {
  const s = siglaDe(valor);
  return Boolean(s && EMPRESAS[s]);
};

/* Las siglas que no reconocemos, con cuántos choferes tiene cada una. Misma
 * forma que `zona.desconocidas`, y por el mismo motivo: agrupadas se leen como
 * un problema y no como veinte. */
export function desconocidas(filas) {
  const cuenta = new Map();
  for (const f of filas || []) {
    const s = siglaDe(f?.empresa);
    if (!s || EMPRESAS[s]) continue;
    cuenta.set(s, (cuenta.get(s) || 0) + 1);
  }
  return [...cuenta.entries()]
    .map(([sigla, choferes]) => ({ sigla, choferes }))
    .sort((a, b) => b.choferes - a.choferes || a.sigla.localeCompare(b.sigla));
}


/* ── Las cuatro empresas propias, para un desplegable ─────────────────────
 *
 * QUÉ ARREGLA. El alta de una persona ofrecía **dos** empresas de cuatro:
 * la lista salía de `empresas.tipo = 'propia'`, y en esa tabla TC figura como
 * «FILTER GROUP S.A» y TA como «TAYLOR», las dos marcadas `proveedor`. Con lo
 * cual a la mitad de la gente no se le podía cargar la empresa — y es el campo
 * que después decide de qué nómina sale.
 *
 * NO SE ARREGLA TOCANDO ESAS DOS FILAS. Llevan `codigo` de proveedor (56 y 57)
 * y el módulo de repuestos las usa por ahí; cambiarles el `tipo` las sacaría
 * de ese desplegable para arreglar éste. Se toman de acá, que es el glosario
 * que ya manda en el resto del hub —los chips, el CUIT al lado de la sigla, la
 * nómina— y en `empresa_catalog.GLOSARIO` de FlotaBot.
 *
 * EL VALOR ES EL CUIT y no la sigla: es lo que guardan `personas.empresa`,
 * `tractores.empresa` y `furgones.empresa`. Guardar la sigla haría que la fila
 * nueva no matchee con las 2.400 que ya están.
 */
export const PROPIAS = Object.entries(EMPRESAS)
  .filter(([, e]) => e.propia)
  .map(([sigla, e]) => ({ sigla, nombre: e.nombre, cuit: CUIT_POR_SIGLA[sigla] }))
  .filter((e) => e.cuit)
  .sort((a, b) => a.sigla.localeCompare(b.sigla));

/* Cómo se ofrecen: la sigla primero, que es como las nombra todo el mundo
 * acá, y el nombre completo detrás para el que recién llega. */
export const opcionesPropias = () =>
  PROPIAS.map((e) => ({
    value: e.cuit,
    label: `${e.sigla} — ${e.nombre}`,
    raw: e,
  }));
